if (!customElements.get('yh-free-gifts')) {
  customElements.define('yh-free-gifts', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.productInfo = this.closest('product-info');
      this.choices = Array.from(this.querySelectorAll('[data-gift-choice]'));
      this.pools = Array.from(this.querySelectorAll('[data-gift-pool-panel]'));
      this.activeChoices = [];
      this.properties = Array.from(this.querySelectorAll('[data-gift-property]'));
      this.limitProperty = this.querySelector('[data-gift-limit-property]');
      this.shippingProperty = this.querySelector('[data-gift-shipping-property]');
      this.propertyInputs = [...this.properties, this.limitProperty, this.shippingProperty].filter(Boolean);
      this.selection = [];
      this.limit = 0;
      this.pending = false;
      this.requiredSelection = this.dataset.required !== 'false';
      this.optionNames = this.parseArray(this.dataset.optionNames);
      this.initialOptions = this.parseArray(this.dataset.initialOptions);
      this.onChange = this.handleChange.bind(this);
      this.onCheckout = this.handleCheckout.bind(this);
      this.onInvalid = () => this.setMessage(this.message('requiredMessage'));
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.variantUnsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
          if (String(data?.sectionId) === String(this.productInfo?.dataset.originalSection || this.productInfo?.dataset.section)) {
            this.refreshFromVariant();
          }
        });
      }
      this.productInfo?.addEventListener('change', this.onChange);
      this.productInfo?.addEventListener('click', this.onCheckout, true);
      this.productInfo?.addEventListener('keydown', this.onCheckout, true);
      this.addEventListener('invalid', this.onInvalid, true);
      this.refreshFromVariant();
    }

    disconnectedCallback() {
      this.productInfo?.removeEventListener('change', this.onChange);
      this.productInfo?.removeEventListener('click', this.onCheckout, true);
      this.productInfo?.removeEventListener('keydown', this.onCheckout, true);
      this.removeEventListener('invalid', this.onInvalid, true);
      this.variantUnsubscribe?.();
      this.choices?.forEach((choice) => choice.setCustomValidity(''));
      this.propertyInputs?.forEach((input) => {
        input.disabled = true;
        input.value = '';
        this.append(input);
      });
      this.initialized = false;
    }

    get form() {
      const form = document.getElementById(this.dataset.formId);
      return this.productInfo?.contains(form) ? form : null;
    }

    parseArray(value) {
      try {
        const parsed = JSON.parse(value || '[]');
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }

    message(key) {
      return (this.dataset[key] || '')
        .replaceAll('__limit__', String(this.limit))
        .replaceAll('__total__', String(this.capacity || 0))
        .replaceAll('__count__', String(this.selection.length));
    }

    matchesKeywords(value, keywords) {
      const text = String(value || '').normalize('NFKC').toLowerCase();
      return String(keywords || '').split(/[,，\n]/).some((keyword) => {
        const term = keyword.trim().normalize('NFKC').toLowerCase();
        if (!term) return false;
        if (/[\u3400-\u9fff]/u.test(term)) return text.includes(term);
        const escaped = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        return new RegExp(`(^|[^\\p{L}\\p{N}])${escaped}($|[^\\p{L}\\p{N}])`, 'iu').test(text);
      });
    }

    shippingFor(value) {
      const sea = this.matchesKeywords(value, this.dataset.seaKeywords);
      const air = this.matchesKeywords(value, this.dataset.airKeywords);
      if (sea === air) return null;
      return air ? { method: 'air', limit: 3 } : { method: 'sea', limit: 2 };
    }

    ruleFor(options) {
      const name = (this.dataset.shippingOption || 'shipping').trim().toLowerCase();
      const index = this.optionNames.findIndex((option) => String(option).trim().toLowerCase() === name);
      if (index >= 0) return this.shippingFor(options[index]);
      const matches = options.map((option) => this.shippingFor(option)).filter(Boolean);
      return matches.length === 1 ? matches[0] : null;
    }

    get variantPicker() {
      return Array.from(this.productInfo?.querySelectorAll('variant-selects') || [])
        .find((picker) => !picker.hidden && picker.style.display !== 'none');
    }

    selectedOptions(picker = this.variantPicker) {
      if (!picker) return this.initialOptions;
      const selected = Array.from(picker.querySelectorAll('input[type="radio"]:checked, select'));
      const fields = Array.from(picker.querySelectorAll('fieldset, .product-form__input--dropdown'));
      return this.optionNames.map((name, index) => {
        const input = selected.find((element) => {
          const optionName = element.dataset.optionName || element.selectedOptions?.[0]?.dataset.optionName;
          return optionName === name;
        });
        return input?.value ?? fields[index]?.querySelector('input:checked, select')?.value ?? this.initialOptions[index];
      });
    }

    refreshFromVariant() {
      const picker = this.variantPicker;
      const options = this.selectedOptions(picker);
      let variant;
      try {
        variant = JSON.parse(picker?.querySelector('[data-selected-variant]')?.textContent || 'null');
      } catch {
        variant = null;
      }
      const variantId = this.form?.elements.namedItem('id')?.value;
      this.pending = Boolean(picker && (!variantId || !variant ||
        String(variant.id) !== String(variantId) ||
        !options.every((option, index) => option === variant.options?.[index])));
      this.applyRule(this.ruleFor(options));
    }

    handleChange(event) {
      const input = event.target;
      if (this.contains(input) && input.matches('[data-gift-choice]')) {
        if (input.checked) {
          if (!this.poolReady || input.dataset.giftMethod !== this.shipping ||
            !this.limit || this.selection.length >= this.limit) {
            input.checked = false;
            this.setMessage(this.message('limitMessage'));
            return;
          }
          if (!this.selection.includes(input.value)) this.selection.push(input.value);
        } else {
          this.selection = this.selection.filter((value) => value !== input.value);
        }
        this.setMessage('');
        this.render();
      } else if (input.name === 'id' && input.form === this.form) {
        this.refreshFromVariant();
      } else if (input.closest('variant-selects')?.closest('product-info') === this.productInfo) {
        if (input.closest('variant-selects') === this.variantPicker) this.refreshFromVariant();
      }
    }

    applyRule(rule) {
      const previousCount = this.selection.length;
      const previousLimit = this.limit;
      const previousShipping = this.shipping;
      this.limit = rule?.limit || 0;
      this.shipping = rule?.method || '';
      this.capacity = this.shipping === 'air' ? 6 : this.shipping === 'sea' ? 4 : 0;
      this.activePool = this.pools.find((pool) => pool.dataset.giftPoolPanel === this.shipping);
      this.activeChoices = this.choices.filter((choice) => choice.dataset.giftMethod === this.shipping);
      this.poolReady = this.activePool?.dataset.ready === 'true' && this.activeChoices.length === this.capacity;
      const allowed = new Set(this.activeChoices.map((choice) => choice.value));
      this.selection = previousShipping !== this.shipping || !this.poolReady ? [] :
        this.selection.filter((value) => allowed.has(value)).slice(0, this.limit);
      if (previousShipping && this.shipping && previousShipping !== this.shipping && previousCount) {
        this.setMessage(this.message('changedMessage'));
      } else if (this.limit && previousCount > this.limit) {
        this.setMessage(this.message('reducedMessage'));
      } else if (previousLimit !== this.limit) {
        this.setMessage('');
      }
      this.render();
    }

    handleCheckout(event) {
      if (event.type === 'keydown' && !['Enter', ' ', 'Spacebar'].includes(event.key)) return;
      const wrapper = event.target.closest?.('[data-yh-dynamic-checkout]');
      if (!wrapper || !this.form?.contains(wrapper)) return;
      if (!this.validateSelection()) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    }

    render() {
      const form = this.form;
      const active = Boolean(this.limit && form && this.poolReady);
      if (form) this.propertyInputs.forEach((input) => {
        if (input.parentElement !== form) form.append(input);
      });
      this.hidden = !active;
      this.dataset.active = String(active);
      this.pools.forEach((pool) => {
        pool.hidden = !active || pool !== this.activePool;
      });
      this.querySelector('[data-gift-instruction]').textContent = this.message(this.requiredSelection ? 'choose' : 'chooseUpTo');
      this.querySelector('[data-gift-progress]').textContent = this.message('progress');
      this.querySelector('[data-gift-shipping]').textContent = this.message(this.shipping === 'air' ? 'airLabel' : 'seaLabel');
      this.choices.forEach((choice) => {
        choice.checked = this.selection.includes(choice.value);
        choice.disabled = !active || choice.dataset.giftMethod !== this.shipping ||
          (!choice.checked && this.selection.length >= this.limit);
        choice.setCustomValidity('');
      });
      if (active && !this.pending && this.requiredSelection && this.selection.length !== this.limit) {
        this.activeChoices[0]?.setCustomValidity(this.message('requiredMessage'));
      }
      const changedInputs = [];
      const updateProperty = (property, value, disabled) => {
        if (property.value !== value || property.disabled !== disabled) changedInputs.push(property);
        property.value = value;
        property.disabled = disabled;
      };
      this.properties.forEach((property, index) => {
        const choice = this.activeChoices.find((item) => item.value === this.selection[index]);
        updateProperty(property, choice?.dataset.giftName || '', !active || this.pending || !choice);
      });
      const metadataDisabled = !active || this.pending || !this.selection.length;
      updateProperty(this.limitProperty, String(this.limit), metadataDisabled);
      updateProperty(this.shippingProperty, this.shipping, metadataDisabled);
      changedInputs.forEach((input) => input.dispatchEvent(new Event('change', { bubbles: true })));
      if (!this.pending && this.querySelector('[data-gift-message]').textContent === this.message('waitMessage')) {
        this.setMessage('');
      }
    }

    setMessage(text) {
      const message = this.querySelector('[data-gift-message]');
      message.textContent = text;
      message.hidden = !text;
    }

    validateSelection() {
      this.refreshFromVariant();
      if (!this.limit || !this.form || !this.poolReady) return true;
      if (this.pending) {
        this.setMessage(this.message('waitMessage'));
        return false;
      }
      if (this.requiredSelection && this.selection.length !== this.limit) {
        this.setMessage(this.message('requiredMessage'));
        const target = this.activeChoices.find((choice) => !choice.checked && !choice.disabled) || this.activeChoices[0];
        this.scrollIntoView({ block: 'center', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
        target?.focus({ preventScroll: true });
        return false;
      }
      return true;
    }
  });
}
